import { Children, useLayoutEffect, useRef, useState, type ReactNode } from 'react';

const SIZE_PX = { sm: 38, md: 56, lg: 74, xl: 92 } as const;

/**
 * A fanned hand that always fits its container: the overlap between cards is computed from the available
 * width so every card stays visible and tappable on small phones (and spreads out on desktops).
 */
export function Hand({ children, size = 'lg', label, maxStep = 0.72 }: { children: ReactNode; size?: keyof typeof SIZE_PX; label: string; maxStep?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [width, setWidth] = useState(360);
  const items = Children.toArray(children).filter(Boolean);
  useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const ro = new ResizeObserver(([e]) => setWidth(e!.contentRect.width));
    ro.observe(el);
    setWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);
  const n = items.length;
  // Landscape phones (very little height): smaller cards leave the table and the thrown cards room.
  const short = typeof window !== 'undefined' && window.innerHeight <= 520 && window.innerWidth > window.innerHeight;
  let w = short ? Math.min(SIZE_PX[size], 56) : SIZE_PX[size];
  // Shrink cards if even a tight fan would overflow.
  while (n > 1 && w > 40 && w + (n - 1) * w * 0.28 > width) w -= 4;
  const step = n > 1 ? Math.min(w * maxStep, Math.max(w * 0.28, (width - w) / (n - 1))) : w;
  return (
    <div ref={ref} className="hand fit" role="group" aria-label={label} style={{ ['--fit-w' as string]: `${w}px` }}>
      {items.map((child, i) => (
        <div key={(child as { key?: string | null }).key ?? i} className="hand-slot" style={{ marginLeft: i === 0 ? 0 : step - w, zIndex: i }}>
          {child}
        </div>
      ))}
    </div>
  );
}
